import React from 'react';
import { cn } from '../../utils/cn';

/**
 * Skeleton Component
 * 
 * A placeholder UI component that mimics the structure of content while data is loading.
 * Uses Apple-inspired design patterns with shimmer animation.
 * 
 * Requirements: 3.1, 3.2, 3.6
 * - 3.1: Render placeholder shapes that match the layout of actual content
 * - 3.2: Display shimmer animation to indicate loading activity
 * - 3.6: Use CSS variables from the Design_System for consistent styling
 */

export interface SkeletonProps {
  /** The shape variant of the skeleton */
  variant: 'text' | 'circular' | 'rectangular' | 'rounded';
  /** Width of the skeleton (CSS value or number in pixels) */
  width?: string | number;
  /** Height of the skeleton (CSS value or number in pixels) */
  height?: string | number;
  /** Animation type for the skeleton */
  animation?: 'shimmer' | 'pulse' | 'none';
  /** Additional CSS classes */
  className?: string;
}

/**
 * Converts a dimension value to a CSS string
 */
const toCssValue = (value: string | number | undefined): string | undefined => {
  if (value === undefined) return undefined;
  if (typeof value === 'number') return `${value}px`;
  return value;
};

/**
 * Base Skeleton component for loading states
 * 
 * @example
 * // Text skeleton (single line)
 * <Skeleton variant="text" width="80%" />
 * 
 * @example
 * // Circular skeleton (avatar)
 * <Skeleton variant="circular" width={48} height={48} />
 * 
 * @example
 * // Rectangular skeleton (image placeholder)
 * <Skeleton variant="rectangular" width="100%" height={200} />
 * 
 * @example
 * // Rounded skeleton (card)
 * <Skeleton variant="rounded" width="100%" height={120} />
 */
const Skeleton: React.FC<SkeletonProps> = ({
  variant,
  width,
  height,
  animation = 'shimmer',
  className,
}) => {
  // Determine default dimensions based on variant
  const getDefaultDimensions = () => {
    switch (variant) {
      case 'text':
        return {
          width: width ?? '100%',
          height: height ?? '1em',
        };
      case 'circular':
        return {
          width: width ?? 40,
          height: height ?? width ?? 40,
        };
      case 'rectangular':
        return {
          width: width ?? '100%',
          height: height ?? 100,
        };
      case 'rounded':
        return {
          width: width ?? '100%',
          height: height ?? 100,
        };
      default:
        return {
          width: width ?? '100%',
          height: height ?? 'auto',
        };
    }
  };

  const dimensions = getDefaultDimensions();

  // Get variant-specific border radius
  const getVariantClasses = (): string => {
    switch (variant) {
      case 'text':
        // Text uses small radius for a subtle rounded effect
        return 'rounded-[var(--radius-sm)]';
      case 'circular':
        // Circular uses full radius for perfect circle
        return 'rounded-full';
      case 'rectangular':
        // Rectangular has no border radius
        return 'rounded-none';
      case 'rounded':
        // Rounded uses medium radius for card-like appearance
        return 'rounded-[var(--radius-md)]';
      default:
        return '';
    }
  };

  // Get animation classes
  const getAnimationClasses = (): string => {
    switch (animation) {
      case 'shimmer':
        // Uses the shimmer animation defined in design-tokens.css
        return 'skeleton-shimmer';
      case 'pulse':
        // Pulse animation using Tailwind's built-in animate-pulse
        return 'animate-pulse bg-[var(--color-background-tertiary)]';
      case 'none':
        // No animation, just static background
        return 'bg-[var(--color-background-tertiary)]';
      default:
        return 'skeleton-shimmer';
    }
  };

  const style: React.CSSProperties = {
    width: toCssValue(dimensions.width),
    height: toCssValue(dimensions.height),
  };

  return (
    <div
      className={cn(
        // Base styles
        'block',
        // Variant-specific border radius
        getVariantClasses(),
        // Animation styles
        getAnimationClasses(),
        // Custom classes
        className
      )}
      style={style}
      role="presentation"
      aria-hidden="true"
      data-testid="skeleton"
    />
  );
};

/**
 * SkeletonCard Component
 * 
 * A skeleton variant that matches the FileCard layout structure.
 * Used as a placeholder while file card data is loading.
 * 
 * Requirements: 3.1, 3.4
 * - 3.1: Render placeholder shapes that match the layout of actual content
 * - 3.4: Support variants for file cards
 */

export interface SkeletonCardProps {
  /** Whether to show the thumbnail placeholder */
  showThumbnail?: boolean;
  /** Whether to show the tags placeholder */
  showTags?: boolean;
  /** Whether to show the meta info placeholder (type and size) */
  showMeta?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SkeletonCard component for file card loading states
 * 
 * Matches the layout structure of FileCard component:
 * - Thumbnail area (aspect-video)
 * - Title
 * - Permission indicator
 * - Tags (optional)
 * - Meta info: type and size (optional)
 * 
 * @example
 * // Full skeleton card
 * <SkeletonCard />
 * 
 * @example
 * // Without tags
 * <SkeletonCard showTags={false} />
 * 
 * @example
 * // Minimal skeleton (thumbnail and title only)
 * <SkeletonCard showTags={false} showMeta={false} />
 */
export const SkeletonCard: React.FC<SkeletonCardProps> = ({
  showThumbnail = true,
  showTags = true,
  showMeta = true,
  className,
}) => {
  return (
    <div
      className={cn(
        // Match FileCard container styles
        'relative flex flex-col overflow-hidden bg-white border border-gray-200 rounded-lg shadow-sm',
        className
      )}
      role="presentation"
      aria-hidden="true"
      data-testid="skeleton-card"
    >
      {/* Thumbnail area - matches FileCard aspect-video */}
      {showThumbnail && (
        <div className="relative aspect-video bg-[var(--color-background-secondary)] overflow-hidden">
          <Skeleton
            variant="rectangular"
            width="100%"
            height="100%"
            animation="shimmer"
            className="absolute inset-0"
          />
        </div>
      )}

      {/* Content area - matches FileCard p-3 padding */}
      <div className="flex flex-col flex-1 p-3">
        {/* Title skeleton - matches FileCard h3 */}
        <Skeleton
          variant="text"
          width="75%"
          height={16}
          animation="shimmer"
          className="mb-2"
        />

        <div className="mt-auto">
          {/* Permission indicator skeleton */}
          <div className="flex items-center gap-2 mb-2">
            <Skeleton
              variant="circular"
              width={12}
              height={12}
              animation="shimmer"
            />
            <Skeleton
              variant="rounded"
              width={40}
              height={16}
              animation="shimmer"
            />
          </div>

          {/* Tags skeleton */}
          {showTags && (
            <div className="flex flex-wrap gap-1 mb-2">
              <Skeleton
                variant="rounded"
                width={56}
                height={20}
                animation="shimmer"
              />
              <Skeleton
                variant="rounded"
                width={48}
                height={20}
                animation="shimmer"
              />
            </div>
          )}

          {/* Meta info skeleton - type and size */}
          {showMeta && (
            <div className="flex items-center justify-between">
              <Skeleton
                variant="text"
                width={50}
                height={12}
                animation="shimmer"
              />
              <Skeleton
                variant="text"
                width={40}
                height={12}
                animation="shimmer"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * SkeletonGrid Component
 * 
 * A skeleton variant that renders multiple SkeletonCard components in a grid layout.
 * Matches the FileGrid component's responsive grid structure.
 * 
 * Requirements: 3.4
 * - 3.4: Support variants for file grids
 */

export interface SkeletonGridProps {
  /** Number of skeleton cards to render (default: 8) */
  count?: number;
  /** Number of columns for the grid (default: responsive based on viewport) */
  columns?: number;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SkeletonGrid component for file grid loading states
 * 
 * Renders multiple SkeletonCard components in a responsive grid layout
 * that matches the FileGrid component structure.
 * 
 * @example
 * // Default grid (8 items, responsive columns)
 * <SkeletonGrid />
 * 
 * @example
 * // Custom count
 * <SkeletonGrid count={12} />
 * 
 * @example
 * // Fixed columns
 * <SkeletonGrid count={6} columns={3} />
 */
export const SkeletonGrid: React.FC<SkeletonGridProps> = ({
  count = 8,
  columns,
  className,
}) => {
  // Generate grid column classes based on columns prop
  // If columns is specified, use fixed columns; otherwise use responsive defaults
  const getGridClasses = (): string => {
    if (columns) {
      // Fixed column count
      switch (columns) {
        case 1:
          return 'grid-cols-1';
        case 2:
          return 'grid-cols-2';
        case 3:
          return 'grid-cols-3';
        case 4:
          return 'grid-cols-4';
        case 5:
          return 'grid-cols-5';
        case 6:
          return 'grid-cols-6';
        default:
          // For larger column counts, use CSS custom property
          return '';
      }
    }
    // Responsive columns matching FileGrid layout
    return 'grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5';
  };

  // Custom style for non-standard column counts
  const gridStyle: React.CSSProperties = columns && columns > 6
    ? { gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }
    : {};

  return (
    <div
      className={cn(
        'grid gap-4',
        getGridClasses(),
        className
      )}
      style={gridStyle}
      role="presentation"
      aria-hidden="true"
      aria-label="Loading content"
      data-testid="skeleton-grid"
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonCard key={index} />
      ))}
    </div>
  );
};

/**
 * SkeletonList Component
 * 
 * A skeleton variant that renders multiple list item placeholders.
 * Matches the FileList component's layout structure for both desktop and mobile views.
 * 
 * Requirements: 3.4
 * - 3.4: Support variants for file lists
 */

export interface SkeletonListProps {
  /** Number of skeleton list items to render (default: 5) */
  count?: number;
  /** Whether to show the file type icon placeholder (default: true) */
  showIcon?: boolean;
  /** Additional CSS classes */
  className?: string;
}

/**
 * SkeletonListItem Component (Internal)
 * 
 * A single skeleton list item that matches the FileList row structure.
 * Renders differently for desktop (table row) and mobile (stacked) views.
 */
interface SkeletonListItemProps {
  showIcon?: boolean;
}

const SkeletonListItem: React.FC<SkeletonListItemProps> = ({ showIcon = true }) => {
  return (
    <>
      {/* Desktop table row - hidden on mobile */}
      <tr className="hidden md:table-row" role="presentation" aria-hidden="true">
        {/* Name column with icon */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex items-center">
            {showIcon && (
              <div className="flex-shrink-0 h-12 w-12 rounded-lg overflow-hidden">
                <Skeleton
                  variant="rounded"
                  width={48}
                  height={48}
                  animation="shimmer"
                />
              </div>
            )}
            <div className={cn("flex flex-col gap-1", showIcon && "ml-4")}>
              {/* File name */}
              <Skeleton
                variant="text"
                width={180}
                height={14}
                animation="shimmer"
              />
              {/* Description (optional) */}
              <Skeleton
                variant="text"
                width={120}
                height={12}
                animation="shimmer"
              />
            </div>
          </div>
        </td>
        {/* Type column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton
            variant="rounded"
            width={64}
            height={22}
            animation="shimmer"
          />
        </td>
        {/* Size column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton
            variant="text"
            width={48}
            height={14}
            animation="shimmer"
          />
        </td>
        {/* Modified date column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <Skeleton
            variant="text"
            width={100}
            height={14}
            animation="shimmer"
          />
        </td>
        {/* Tags column */}
        <td className="px-6 py-4 whitespace-nowrap">
          <div className="flex flex-wrap gap-1">
            <Skeleton
              variant="rounded"
              width={48}
              height={20}
              animation="shimmer"
            />
            <Skeleton
              variant="rounded"
              width={40}
              height={20}
              animation="shimmer"
            />
          </div>
        </td>
        {/* Actions column */}
        <td className="px-6 py-4 whitespace-nowrap text-right">
          <div className="flex items-center justify-end space-x-2">
            <Skeleton
              variant="circular"
              width={36}
              height={36}
              animation="shimmer"
            />
            <Skeleton
              variant="circular"
              width={36}
              height={36}
              animation="shimmer"
            />
          </div>
        </td>
      </tr>

      {/* Mobile stacked row - hidden on desktop */}
      <div
        className="md:hidden flex items-center py-3 border-b border-gray-200 last:border-b-0"
        role="presentation"
        aria-hidden="true"
      >
        <div className="flex-1 min-w-0">
          {/* File name */}
          <Skeleton
            variant="text"
            width="70%"
            height={14}
            animation="shimmer"
            className="mb-1"
          />
          {/* Meta info: type · size · date */}
          <Skeleton
            variant="text"
            width="50%"
            height={12}
            animation="shimmer"
          />
        </div>
        {/* Action buttons */}
        <div className="flex items-center gap-1 ml-2">
          <Skeleton
            variant="circular"
            width={36}
            height={36}
            animation="shimmer"
          />
          <Skeleton
            variant="circular"
            width={36}
            height={36}
            animation="shimmer"
          />
        </div>
      </div>
    </>
  );
};

/**
 * SkeletonList component for file list loading states
 * 
 * Renders multiple skeleton list items that match the FileList component structure.
 * Supports both desktop table view and mobile stacked view.
 * 
 * @example
 * // Default list (5 items with icons)
 * <SkeletonList />
 * 
 * @example
 * // Custom count
 * <SkeletonList count={10} />
 * 
 * @example
 * // Without icons
 * <SkeletonList count={5} showIcon={false} />
 */
export const SkeletonList: React.FC<SkeletonListProps> = ({
  count = 5,
  showIcon = true,
  className,
}) => {
  return (
    <div
      className={cn('space-y-4', className)}
      role="presentation"
      aria-hidden="true"
      aria-label="Loading content"
      data-testid="skeleton-list"
    >
      {/* Desktop table view */}
      <div className="hidden md:block overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-5/12">
                <Skeleton variant="text" width={40} height={10} animation="shimmer" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">
                <Skeleton variant="text" width={32} height={10} animation="shimmer" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-1/12">
                <Skeleton variant="text" width={28} height={10} animation="shimmer" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">
                <Skeleton variant="text" width={52} height={10} animation="shimmer" />
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-2/12">
                <Skeleton variant="text" width={32} height={10} animation="shimmer" />
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {Array.from({ length: count }).map((_, index) => (
              <SkeletonListItem key={index} showIcon={showIcon} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile stacked view */}
      <div className="md:hidden divide-y divide-gray-200">
        {Array.from({ length: count }).map((_, index) => (
          <SkeletonListItem key={index} showIcon={showIcon} />
        ))}
      </div>
    </div>
  );
};

export default Skeleton;
